import { useState, useEffect, useMemo } from 'react';
import { getReferees } from '@/api/TournamentManagement/refereeApi';
import { getCompensationReport } from '@/api/TournamentManagement/workPointApi';
import type { RefereeView, CompensationReportView } from '@/models/tournament';
import { RefereeCertification } from '@/models/tournament';

export interface RefereeWorkloadRow {
  userId: string;
  name: string;
  certification: RefereeCertification;
  rating: number;
  matchCount: number;
  workPoints: number;
}

export interface UseRefereeWorkloadResult {
  data: RefereeWorkloadRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function getDefaultRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    start: start.toISOString().split('T')[0],
    end: now.toISOString().split('T')[0],
  };
}

export function useRefereeWorkload(dateRange?: { start: string; end: string }): UseRefereeWorkloadResult {
  const [referees, setReferees] = useState<RefereeView[]>([]);
  const [report, setReport] = useState<CompensationReportView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const range = useMemo(() => dateRange ?? getDefaultRange(), [dateRange?.start, dateRange?.end]);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setError(null);

      try {
        const [refResult, reportResult] = await Promise.all([
          getReferees({ pageSize: 500 }),
          getCompensationReport({ startDate: range.start, endDate: range.end }),
        ]);

        if (cancelled) return;

        if (refResult.errors.length > 0) {
          setError(refResult.errors[0]?.description || 'Failed to load referees');
          return;
        }

        setReferees(refResult.payload?.items ?? []);
        setReport(reportResult.errors.length === 0 ? reportResult.payload : null);
      } catch {
        if (!cancelled) {
          setError('Failed to load workload data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [fetchKey, range.start, range.end]);

  const data = useMemo<RefereeWorkloadRow[]>(() => {
    if (referees.length === 0) return [];

    const reportMap = new Map<string, { matchCount: number; workPoints: number }>();
    if (report) {
      for (const line of report.lines) {
        reportMap.set(line.refereeUserId, {
          matchCount: line.matchCount,
          workPoints: line.totalWorkPoints,
        });
      }
    }

    return referees.map((ref) => {
      const refData = reportMap.get(ref.userId);
      return {
        userId: ref.userId,
        name: ref.name,
        certification: ref.certification,
        rating: ref.rating,
        matchCount: refData?.matchCount ?? 0,
        workPoints: refData?.workPoints ?? 0,
      };
    });
  }, [referees, report]);

  const refetch = () => setFetchKey((k) => k + 1);

  return { data, loading, error, refetch };
}
